import React from 'react';
import { Box, Typography, useTheme, Tooltip, IconButton } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { formatChartTitle, formatTrendPercent } from '../../utils';

interface TextStatProps {
  title: string;
  value: string;
  change?: string | null;
  unit?: string;
  changeTooltip?: string;
}

export const TextStat: React.FC<TextStatProps> = ({ title, value, change, unit, changeTooltip }) => {
  const theme = useTheme();
  const formattedTitle = formatChartTitle(title);
  const normalizedChange = formatTrendPercent(change) ?? '';
  const isNegativeChange = normalizedChange.trim().startsWith('-');
  const isPositiveChange = normalizedChange.trim().startsWith('+');
  const trendColor = isNegativeChange
    ? theme.palette.error.main
    : isPositiveChange
      ? theme.palette.success.main
      : theme.palette.text.secondary;
  const TrendIcon = isNegativeChange ? TrendingDownIcon : isPositiveChange ? TrendingUpIcon : TrendingFlatIcon;

  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant='caption'
        sx={{
          color: 'text.secondary',
        }}
      >
        {formattedTitle}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 0.75,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
          <Typography
            variant='subtitle1'
            component='span'
            sx={{
              fontWeight: 700,
            }}
          >
            {value}
          </Typography>
          {unit && (
            <Typography variant='caption' color='text.secondary'>
              {unit}
            </Typography>
          )}
        </Box>
        {change && (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.25,
              color: trendColor,
            }}
          >
            <Typography
              component='span'
              sx={{
                fontWeight: 600,
                fontSize: '0.875rem',
              }}
            >
              {normalizedChange}
            </Typography>
            <TrendIcon
              sx={{
                fontSize: '0.875rem',
                color: trendColor,
              }}
            />
            <Tooltip
              title={
                <Box sx={{ p: 0.5 }}>
                  <Typography variant='body2' sx={{ fontWeight: 600, mb: 0.5 }}>
                    How this change is calculated
                  </Typography>
                  <Typography variant='body2'>
                    {changeTooltip ||
                      'Compares the average value over the selected period against the equal-length period immediately before it. Formula: ((current average − previous average) ÷ previous average) × 100'}
                  </Typography>
                </Box>
              }
              arrow
              placement='top'
            >
              <IconButton
                size='small'
                sx={{
                  p: 0,
                  ml: 0.25,
                  color: 'text.disabled',
                  '&:hover': { color: 'text.secondary' },
                }}
                aria-label='Change calculation info'
              >
                <InfoOutlinedIcon sx={{ fontSize: '0.75rem' }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>
    </Box>
  );
};
